import { useSearchParamsContext } from '@internal/blocks/SearchResultsBlock/SearchParamsContext'
import { getCountryName, loadCountriesLocale } from '@internal/utils/countries'
import { localeNamespace, TLocale } from '@internal/utils/machine/i18n/Locale'
import { LocalShippingOutlined } from '@mui/icons-material'
import { TFilterOption } from '@renderer-ui-library/molecules'
import { useCustomTags } from '@renderer-ui-library/molecules/AutoComplete/useCustomTags'
import {
  groupBy,
  renderGroup,
} from '@renderer-ui-library/molecules/RemoteAutocomplete/SelectItem'
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  CircularProgress,
  TextField,
  Typography,
} from '@renderer-ui-library/mui'
import { useSearchResultsRouting } from 'blocks/SearchResultsBlock/useSearchResultsRouting'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { tracker } from 'utils/tracking/tracker'
import { useAutocompleteItem } from './AutocompleteItem'
import { SearchFilterCategory } from './SearchFilterCategory/SearchFilterCategory'
import styles from './locationFilter.module.scss'

const popularCountries = ['DE', 'FR', 'PL', 'DK', 'AT']
const otherCountries = [
  'RU',
  'GB',
  'IT',
  'ES',
  'UA',
  'RO',
  'NL',
  'BE',
  'CZ',
  'GR',
  'PT',
  'SE',
  'HU',
  'BY',
  'RS',
  'CH',
  'BG',
  'FI',
  'SK',
  'NO',
  'IE',
  'HR',
  'MD',
  'BA',
  'AL',
  'LT',
  'MK',
  'SI',
  'LV',
  'EE',
  'ME',
  'LU',
  'MT',
  'IS',
  'AD',
  'MC',
  'LI',
  'SM',
  'VA',
]

const sortCountryOptions = <T extends { name: string }>(a: T, b: T) =>
  a.name.localeCompare(b.name)

enum CountryGroup {
  Popular = 'popular',
  Rest = 'rest',
}

export const LocationFilter: React.FC<{
  locale: TLocale
}> = React.memo((props) => {
  const { searchParams } = useSearchParamsContext()
  const { gotoSearchResults } = useSearchResultsRouting()
  const { t } = useTranslation(localeNamespace.common)
  const { renderOption } = useAutocompleteItem()
  const [isLoading, setIsLoading] = useState(false)
  const [countries, setCountries] = useState<
    { code: string; name: string; group: CountryGroup }[]
  >([])

  const { renderTags } = useCustomTags<TFilterOption, true>()

  useEffect(() => {
    setIsLoading(true)
    void loadCountriesLocale(props.locale)
      .then(() => {
        setCountries([
          ...popularCountries
            .map((code) => ({
              code,
              name: getCountryName(props.locale, code) ?? '',
              group: CountryGroup.Popular,
            }))
            .sort(sortCountryOptions),
          ...otherCountries
            .map((code) => ({
              code,
              name: getCountryName(props.locale, code) ?? '',
              group: CountryGroup.Rest,
            }))
            .sort(sortCountryOptions),
        ])
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [props.locale])

  const options = useMemo<TFilterOption[]>(() => {
    const popularLabel = t(
      translations.searchResultsFiltersLocationPopularLabel
    )
    return countries.map(
      ({ code, name, group }): TFilterOption => ({
        label: name,
        id: code,
        selected: searchParams.locations.includes(code),
        group:
          group === CountryGroup.Popular ? popularLabel : name.substring(0, 1),
      })
    )
  }, [countries, searchParams.locations, t])

  const setLocations = useCallback(
    (locations: string[]) =>
      gotoSearchResults({
        ...searchParams,
        locations,
        amount: undefined,
      }),
    [gotoSearchResults, searchParams]
  )

  const handleChange = useCallback(
    (
      event: React.SyntheticEvent,
      value: TFilterOption[],
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<TFilterOption>
    ) => {
      const newLocations = value.map((option) => option.id)
      const addedLocations = newLocations.filter(
        (loc: string) => !searchParams.locations.includes(loc)
      )
      setLocations(newLocations)

      if (addedLocations.length > 0) {
        tracker.trackEvent({
          name: 'filter-applied',
          filter_placement: 'serp',
          filter_category: 'location',
          filter_location: addedLocations[0],
          is_interaction: true,
        })
      }
    },
    [searchParams.locations, setLocations]
  )

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        placeholder={t(translations.searchResultsFiltersLocationPlaceholder)}
        size='small'
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <React.Fragment>
              {isLoading ? (
                <CircularProgress color='inherit' size={20} />
              ) : null}
              {params.InputProps.endAdornment}
            </React.Fragment>
          ),
        }}
      />
    ),
    [t, isLoading]
  )

  const autocompleteValue = useMemo<TFilterOption[]>(() => {
    return options.filter((option) => option.selected)
  }, [options])

  return (
    <SearchFilterCategory
      title={t(translations.searchResultsFiltersLocationLabel)}
    >
      <Autocomplete<TFilterOption, true>
        multiple
        disableCloseOnSelect
        limitTags={3}
        id='filtered-locations'
        options={options}
        renderOption={renderOption}
        renderGroup={renderGroup}
        renderInput={renderInput}
        value={autocompleteValue}
        onChange={handleChange}
        groupBy={groupBy}
        renderTags={renderTags}
        slotProps={{
          paper: {
            elevation: 10,
          },
        }}
      />
      <div className={styles.locationContentTop}>
        <LocalShippingOutlined className={styles.icon} />
        <Typography component='p' variant='caption' color='textSecondary'>
          {t(translations.searchResultsFiltersLocationContentLabel)}
        </Typography>
      </div>
    </SearchFilterCategory>
  )
})

LocationFilter.displayName = 'LocationFilter'
